import React, {useEffect, useState} from "react";
import {FoodEntry, FoodSource, FoodType, Nutrient, PortionSize} from "../domain/FoodEntry";
import {getCsrfToken, isAdmin, setUserInfo} from "../App";
import {CombinedFoodEntry, CombinedFoodIngredient} from "../domain/CombinedFoodEntry";
import {useNavigate} from "react-router-dom";
import {TextEditor} from "../WebUtils";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAdd, faEdit, faSave, faTrash} from "@fortawesome/free-solid-svg-icons";
import {
    calculateNutrient,
    FoodSearchPopup,
    getFoodCacheEntry,
    readAllFoodEntries,
    readAllFoodEntriesFromCombined
} from "./FoodSearch";
import {PortionSizeEditor} from "../WebFoodEntry";

let nextNegId = -1;
function nextNegativeId() {
    return nextNegId--;
}

export function MultiIngredientEditor() {
    let foodData = CombinedFoodEntry.EMPTY;

    const [nutriId, setNutriId] = useState<number>(0);
    const [publishDetails, setPublishDetails] = useState<string>("New or not loaded yet.");
    const [foodName, setFoodName] = useState<string>("");
    const [category, setCategory] = useState<string>("");
    const [foodPortions, setFoodPortions] = useState<PortionSize[]>([]);
    const [externalId, setExternalId] = useState<string>("0");
    const [publishDate, setPublishDate] = useState<Date>(new Date());
    const [loading, setLoading] = useState<boolean>(true); // To show a "loading" state
    const [error, setError] = useState<string | null>(null);
    const [readOnly, setReadOnly] = useState<boolean>(true);// To handle and display errors
    const [version, setVersion] = useState<number>(0);
    const [ingredients, setIngredients] = useState<CombinedFoodIngredient[]>([]);
    const [cacheVersion, setCacheVersion] = useState<number>(0);
    let navigate = useNavigate();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const param = urlParams.get('id') || "";
        const readParam = urlParams.get('mode') ?? "view";
        const read = readParam === "view";
        setReadOnly(read);

        if (!param || param === "" || param === "0") {
            setLoading(false);
            setError(null);
            return;
        }

        fetch(`/api/v1/foodEntry/combined/${param ?? nutriId}`, {
            method: "GET",
            headers: {"Content-Type": "application/json", "Accept": "application/json"},
        }).then((response) => {
            if (response.status === 401 || response.status === 403) {
                setUserInfo("Guest", "GUEST");
                navigate("/login");
            } else if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json(); // Parse JSON response
        }).then((jsonData) => {
            foodData = CombinedFoodEntry.fromJson(jsonData);
            let foodEntry = foodData.foodEntry;
            if (nutriId !== foodEntry.id) {
                setNutriId(foodEntry.id);
            }
            if(foodEntry.foodType !== FoodType.COMPOSITE_INGREDIENT) {
                setError(`${foodEntry.id}/${foodEntry.name} is not a composite ingredient`);
            } else {
                setError("");
                setNutriId(foodEntry.id);
                setPublishDetails(`Last updated: ${foodEntry.lastUpdated.toLocaleString()} by ${foodEntry.userName} (version ${foodEntry.version})`)
                setFoodPortions(foodEntry.portions);
                setCategory(foodEntry.category);
                setFoodName(foodEntry.name);
                setExternalId(foodEntry.externalId);
                setPublishDate(foodEntry.publicationDate);
                setVersion(foodEntry.version);
                setIngredients(foodData.ingredients);
                readAllFoodEntriesFromCombined(foodData, () => setCacheVersion(cacheVersion + 1));
                setLoading(false); // Turn off loading spinner
            }
            setLoading(false); // Turn off loading spinner
        }).catch((err) => {
            setError(err.message); // Handle errors
            setLoading(false);
        });
    }, [nutriId]);

    function performEditOperation() {
        if (!readOnly) {
            if (foodPortions.length === 0) {
                alert("At least one portion size must be defined.");
            }
            if (foodName.length === 0 || category.length === 0) {
                alert("Name and category must be filled out.");
            }
            const nutrientSource = FoodSource.findByName('NutriCherry Recipe');
            if (!nutrientSource) throw new Error("NutriCherry Recipe source not found");
            let foodEntry = new FoodEntry(nutriId, foodName, category,
                nutrientSource,
                FoodType.COMPOSITE_INGREDIENT, externalId, publishDate, new Date(),
                "WebApp", version, foodPortions, []);
            let combinedEntry = new CombinedFoodEntry(foodEntry, ingredients);

            getCsrfToken().then(token => {
                fetch(`/api/v1/foodEntry/saveCombined`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json', "X-XSRF-TOKEN": token
                    },
                    body: JSON.stringify(combinedEntry.toJson())
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! Status: ${response.status}`);
                        }
                        return response.json();
                    })
                    .then(data => {
                        setNutriId(data.foodEntryId);
                        alert("Record saved successfully. Please refresh the page to see the changes.!");
                    })
                    .catch(error => {
                        alert("Error saving food entry: " + error.message);
                    });
            });
        }

        setReadOnly(!readOnly);
    }

    const editOperationButton = isAdmin() ? <button onClick={e => performEditOperation()}>
        {readOnly ? "Edit" : "Save"} Food</button> : <span/>;

    function addNewIngredient() {
        setIngredients([...ingredients, new CombinedFoodIngredient(foodData.foodEntry.id, nextNegativeId(), 0.5, nextNegativeId())]);
    }

    function ingredientsChanged(ing: CombinedFoodIngredient) {
        setIngredients(ingredients.map(i => i.internalId === ing.internalId ? ing : i));
    }

    function onDeleteIngredients(ing: CombinedFoodIngredient) {
        setIngredients(ingredients.filter(i => i.internalId !== ing.internalId));
    }

    function addNewPortion() {
        const newPortion = new PortionSize(nutriId, nextNegativeId(), "New", "undetermined", 10);
        setFoodPortions([...foodPortions, newPortion]);
    }

    function editPortionFinished(e: PortionSize) {
        setFoodPortions(foodPortions.map(ps => ps.portionSizeId === e.portionSizeId ? e : ps));
    }

    if(loading) return <h1>Page is loading</h1>;
    if(error) return <h1>Error {error}</h1>

    return <div>
        <h2>Edit combined ingredient: {nutriId <= 0 ? "New" : nutriId}</h2>
        <p>{publishDetails}</p>
        <p>
            {editOperationButton}
        </p>

        <h3>Main Food Entry</h3>
        <p>Note that for combined food entries, the type and source are preset.</p>
        <table style={{borderCollapse: 'collapse', width: '100%', marginBottom: '20px', border: '1px solid black'}}>
            <thead>
            <tr>
                <th>Field name</th>
                <th>Value</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td><strong>Name:</strong></td>
                <td><TextEditor size={50} readOnly={readOnly} value={foodName}
                                setter={e => setFoodName(e.target.value)}/></td>
            </tr>
            <tr>
                <td><strong>Category:</strong></td>
                <td><TextEditor size={50} readOnly={readOnly} value={category}
                                setter={e => setCategory(e.target.value)}/></td>
            </tr>
            <tr>
                <td><strong>External ID:</strong></td>
                <td><TextEditor size={30} readOnly={readOnly} value={externalId}
                                setter={e => setExternalId(e.target.value)}/></td>
            </tr>
            <tr>
                <td><strong>Last Published (src):</strong></td>
                {readOnly ? <td>{publishDate.toISOString().slice(0, 10)}</td> :
                    <td><input type="date" value={publishDate.toISOString().slice(0, 10)}
                               onChange={(e) => setPublishDate(new Date(e.target.value))}/></td>
                }
            </tr>
            </tbody>
        </table>

        <h3>Combined Ingredient List</h3>

        <table style={{borderCollapse: 'collapse', width: '100%', marginBottom: '20px', border: '1px solid black'}}>
            <thead>
            <tr>
                <th>Fn</th>
                <th>Ingredient</th>
                <th>Ratio (%)</th>
                <th>Calories</th>
                <th>Fat</th>
                <th>Protein</th>
                <th>Fibre</th>
            </tr>
            </thead>
            <tbody>
            {readOnly ? <span/> : <tr><td colSpan={7}>
                <button onClick={e => addNewIngredient()}><FontAwesomeIcon icon={faAdd}/></button>
            </td></tr> }
            {ingredients.map(ing => <IngredientWithRatio readOnly={readOnly} value={ing} onChange={e => ingredientsChanged(e)}
                                                      deleteFoodEntry={() => onDeleteIngredients(ing)} key={ing.internalId}/>)}

            </tbody>
        </table>
        <p>Total of all ratios make up {(ingredients.reduce((sum, ing) => sum + ing.ratio, 0) * 100).toFixed(2)}%</p>
        <h2>Portion Sizes</h2>
        {!readOnly ? <div>
            <p>Portion sizes are used to calculate the nutritional value of recipes. Edit existing ones with care
                as <b>adjusting them affects live recipes.</b> Further, they cannot be deleted.</p>
            <p>
                <button onClick={addNewPortion}>Add a new portion size</button>
            </p>
        </div> : <span/>}
        <table style={{borderCollapse: 'collapse', width: '100%', border: '1px solid black'}}>
            <thead>
            <tr>
                <th>Portion ID</th>
                <th>Portion Name</th>
                <th>Known Conversion</th>
                <th>Gram Equivalent</th>
            </tr>
            </thead>
            <tbody>
            {foodPortions && foodPortions.map((ps, index) =>
                <PortionSizeEditor portion={ps} readOnly={readOnly} onEdited={e => editPortionFinished(e)}/>)
            }
            </tbody>
        </table>
    </div>;
}

/**
 * @callback FoodIngredientCallback the callback for a food ingredient
 * @param {CombinedFoodIngredient}
 */

/**
 *
 * @param readOnly {boolean} if the item is read only or editing
 * @param value {CombinedFoodIngredient} the ingredient to edit
 * @param onChange {FoodIngredientCallback} when a change occurs that should be saved
 * @param deleteFoodEntry {FoodIngredientCallback} when an ingredient is deleted
 * @param key {number} the key
 * @return {JSX.Element}
 * @constructor
 */
export function IngredientWithRatio({readOnly, value, onChange, deleteFoodEntry}: { readOnly: boolean, value: CombinedFoodIngredient, onChange: (ing: CombinedFoodIngredient) => void, deleteFoodEntry: (ing: CombinedFoodIngredient) => void }) {
    const [ratio, setRatio] = useState<number>(value.ratio * 100.0);
    const [mainFoodEntryId] = useState<number>(value.mainFoodEntryId);
    const [foodEntryId, setFoodEntryId] = useState<number>(value.ingredientEntryId);
    const [internalId] = useState<number>(value.internalId);
    const [editing, setEditing] = useState<boolean>(false);

    function foodEntryChange(fe: FoodEntry) {
        setRatio(50);
        setFoodEntryId(fe.id);
    }

    if(foodEntryId < 0) {
        return <tr><td colSpan={7}><FoodSearchPopup value={""} onFoodSelected={fe => foodEntryChange(fe)}/>
            <button onClick={e => deleteFoodEntry(value)}>Delete line</button>
        </td></tr>
    } else if(getFoodCacheEntry(foodEntryId) == null) {
        return <tr><td colSpan={7}>Loading food entry...</td> </tr>
    }

    const foodEntry = getFoodCacheEntry(foodEntryId);
    if (!foodEntry) return <tr><td colSpan={7}>Error loading food entry</td></tr>;

    function editingChanged() {
        if(editing) {
            onChange(new CombinedFoodIngredient(mainFoodEntryId, foodEntryId, ratio / 100.0, internalId));
        }
        setEditing(!editing);
    }

    return <tr>
        <td>
            {readOnly ? "" : <button onClick={e => editingChanged()}><FontAwesomeIcon icon={editing ? faSave : faEdit }/></button> }
            {readOnly ? "" : <button onClick={e => deleteFoodEntry(value)}><FontAwesomeIcon icon={faTrash}/></button>}
        </td>
        <td>
            {foodEntryId} {foodEntry.name} {!editing ? <span/> : <FoodSearchPopup onFoodSelected={fe => foodEntryChange(fe)} value={value} /> }
        </td>
        <td><TextEditor size={5} value={ratio} readOnly={!editing} setter={e => setRatio(parseFloat(e.target.value))}/></td>
        <td>{calculateNutrient(Nutrient.CALORIES, ratio, foodEntry.nutrients)}</td>
        <td>{calculateNutrient(Nutrient.TOTAL_FAT, ratio, foodEntry.nutrients)}</td>
        <td>{calculateNutrient(Nutrient.PROTEIN, ratio, foodEntry.nutrients)}</td>
        <td>{calculateNutrient(Nutrient.FIBRE, ratio, foodEntry.nutrients)}</td>
    </tr>;
}